import React from 'react';
import './EAInfoTile.scss';
import useLocaleByLanguage from '../../../../utils/hooks/useLocaleByLanguage';
import { format } from 'date-fns';

type Size = 'small' | 'medium' | 'big';
type Color = 'primary' | 'secondary' | 'gray' | 'error' | 'warning' | 'dark';

interface EAInfoTileProps {
  /**
   * Data do wyświetlenia w formacie: miesiąc (body-small) + dzień (H3)
   * Jeśli nie podana, wyświetlane będą children lub icon
   */
  date?: Date;
  /**
   * Ikona do wyświetlenia (ma priorytet ponad children)
   */
  icon?: React.ReactNode;
  /**
   * Custom content do wyświetlenia - liczba, tekst, itp.
   * Wyświetlane tylko gdy date i icon nie są podane
   */
  children?: React.ReactNode;
  /**
   * Rozmiar kafelka: small (40px), medium (64px - domyślny), big (72px)
   */
  size?: Size;
  /**
   * Kolor kafelka z predefiniowaną paletą
   */
  color?: Color;
}

/**
 * Uniwersalny komponent kafelka informacyjnego
 * 
 * Przykłady użycia:
 * 
 * // Wyświetlenie daty
 * <EAInfoTile date={new Date(2025, 9, 6)} size="big" color="primary" />
 * 
 * // Ikona
 * <EAInfoTile size="medium" color="secondary" icon={<IoCheckmarkSharp />} />
 * 
 * // Custom content (liczba, tekst)
 * <EAInfoTile size="medium" color="error">
 *   <span>5</span>
 * </EAInfoTile>
 */
const EAInfoTile: React.FC<EAInfoTileProps> = ({
  date,
  icon,
  children,
  size = 'medium',
  color = 'primary',
}) => {
  const locale = useLocaleByLanguage();

  const renderContent = () => {
    if (date) {
      const month = format(date, 'MMM', { locale }).toUpperCase();
      const day = format(date, 'dd');

      return (
        <div className="info-tile__content">
          <span className="info-tile__month">{month}</span>
          <span className="info-tile__day">{day}</span>
        </div>
      );
    }

    if (icon) {
      return (
        <div className="info-tile__content">
          <span className="info-tile__icon">{icon}</span>
        </div>
      );
    }

    return <div className="info-tile__content">{children}</div>;
  };

  return (
    <div className={`info-tile info-tile--${size} info-tile--${color}`}>
      {renderContent()}
    </div>
  );
};

export default EAInfoTile;